import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';
import HeroSection from './components/HeroSection';
import FeaturedTours from './components/FeaturedTours';
import TourCategories from './components/TourCategories';
import Testimonials from './components/Testimonials';
import BlogPreview from './components/BlogPreview';
import CallToAction from './components/CallToAction';
import WhyChooseUs from './components/WhyChooseUs';
import SEOHead from '../../components/SEO/SEOHead';
import defaultSEO from '../../components/SEO/seoConfig';

const HomePage = () => {
  // Define placeholder data
  const placeholderTours = [
    {
      id: 1,
      title: 'Savannah Safari Adventure',
      description: 'Experience the thrill of the wild in our most famous wildlife reserve.',
      location: 'Savannah National Reserve',
      duration: 4,
      price: 1200,
      rating: 4.8,
      reviews_count: 124,
      image: 'https://source.unsplash.com/random/800x600?safari,wildlife&sig=1',
      category: 'Wildlife'
    },
    {
      id: 2,
      title: 'Tropical Beach Getaway',
      description: 'Relax on pristine white sand beaches along stunning coastlines.',
      location: 'Paradise Beach',
      duration: 5,
      price: 950,
      rating: 4.7,
      reviews_count: 98,
      image: 'https://source.unsplash.com/random/800x600?beach,tropical&sig=2',
      category: 'Beach'
    },
    {
      id: 3,
      title: 'Mountain Climbing Expedition',
      description: 'Conquer majestic peaks with expert guides.',
      location: 'Wildlife Mountain National Park',
      duration: 6,
      price: 1500,
      rating: 4.9,
      reviews_count: 76,
      image: 'https://source.unsplash.com/random/800x600?mountain,climbing&sig=3',
      category: 'Mountain'
    },
    {
      id: 4,
      title: 'Wildlife & Mountain View Safari',
      description: 'Witness wildlife against the backdrop of majestic mountains.',
      location: 'Wildlife National Park',
      duration: 3,
      price: 890,
      rating: 4.6,
      reviews_count: 112,
      image: 'https://source.unsplash.com/random/800x600?wildlife,mountains&sig=4',
      category: 'Wildlife'
    }
  ];

  const placeholderTestimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      country: 'United States',
      avatar: 'https://source.unsplash.com/random/100x100?portrait,woman&sig=1',
      rating: 5,
      comment: 'Our safari with Teem Wildlife Wonders was absolutely incredible! The guides were knowledgeable and friendly, and we saw all of the Big Five. The accommodations were luxurious and the food was delicious. Highly recommend!',
      tour_name: 'Wildlife Safari'
    },
    {
      id: 2,
      name: 'David Chen',
      country: 'Canada',
      avatar: 'https://source.unsplash.com/random/100x100?portrait,man&sig=2',
      rating: 5,
      comment: 'From the moment we landed to our departure, everything was perfectly organized. The beach stay was paradise, and the staff went above and beyond to make our honeymoon special.',
      tour_name: 'Beach & Safari Combo'
    },
    {
      id: 3,
      name: 'Emma Wilson',
      country: 'United Kingdom',
      avatar: 'https://source.unsplash.com/random/100x100?portrait,woman&sig=3',
      rating: 4,
      comment: 'The mountain trek was challenging but so rewarding! Our guide was patient and encouraging. The views were absolutely breathtaking and worth every step.',
      tour_name: 'Mountain Expedition'
    }
  ];

  const placeholderBlogPosts = [
    {
      id: 1,
      title: 'Top 10 Wildlife Photography Tips',
      excerpt: 'Capture stunning wildlife moments with these expert photography techniques.',
      image: 'https://source.unsplash.com/random/800x600?camera,wildlife&sig=1',
      date: '2023-06-15',
      author: 'Michael Roberts',
      category: 'Photography'
    },
    {
      id: 2,
      title: 'Essential Packing Guide for Safari Adventures',
      excerpt: 'Everything you need to bring for a comfortable and memorable safari experience.',
      image: 'https://source.unsplash.com/random/800x600?safari,backpack&sig=2',
      date: '2023-05-22',
      author: 'Lisa Thompson',
      category: 'Travel Tips'
    },
    {
      id: 3,
      title: 'Best Season to Visit East African National Parks',
      excerpt: 'Plan your trip during the optimal seasons for wildlife viewing and comfortable weather.',
      image: 'https://source.unsplash.com/random/800x600?savannah,wildlife&sig=3',
      date: '2023-04-10',
      author: 'James Kimani',
      category: 'Planning'
    }
  ];

  // Fetch featured tours with error handling
  const { 
    data: featuredTours = placeholderTours, 
    isLoading: isLoadingTours 
  } = useQuery('featuredTours', async () => {
    try {
      const response = await api.get('/api/v1/tours/featured');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching featured tours:', error);
      return placeholderTours;
    }
  }, {
    placeholderData: placeholderTours,
    staleTime: 60000,
  });

  // Fetch testimonials with error handling
  const { 
    data: testimonials = placeholderTestimonials, 
    isLoading: isLoadingTestimonials 
  } = useQuery('testimonials', async () => {
    try {
      const response = await api.get('/api/v1/testimonials');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      return placeholderTestimonials;
    }
  }, {
    placeholderData: placeholderTestimonials,
    staleTime: 60000,
  });

  // Fetch blog posts with error handling
  const { 
    data: blogPosts = placeholderBlogPosts, 
    isLoading: isLoadingBlogPosts 
  } = useQuery('blogPosts', async () => {
    try {
      const response = await api.get('/api/v1/blog/recent');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return placeholderBlogPosts;
    }
  }, {
    placeholderData: placeholderBlogPosts,
    staleTime: 60000,
  });

  // Create homepage-specific SEO metadata
  const homepageSEO = {
    title: 'Discover Unforgettable Tours & Safaris',
    description: 'Experience breathtaking wildlife safaris, beach getaways, and mountain expeditions with our premium tour packages. Book your dream vacation today!',
    keywords: ['safari tours', 'wildlife safari', 'beach vacation', 'mountain expedition', 'luxury tours', 'adventure travel', 'Africa tours'],
    canonicalUrl: `${defaultSEO.siteUrl}`,
    ogType: 'website',
    ogImage: `${defaultSEO.siteUrl}/images/home-hero-banner.jpg`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'TravelAgency',
      name: defaultSEO.siteName,
      description: 'Premium tour operator specializing in safari and adventure travel experiences',
      url: defaultSEO.siteUrl,
      logo: `${defaultSEO.siteUrl}/images/logo.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: defaultSEO.contactPhone,
        contactType: 'customer service',
        availableLanguage: ['English', 'Swahili']
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: defaultSEO.address?.street,
        addressLocality: defaultSEO.address?.city,
        addressRegion: defaultSEO.address?.region,
        postalCode: defaultSEO.address?.postalCode,
        addressCountry: defaultSEO.address?.country
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '356'
      },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '500',
        highPrice: '5000',
        priceCurrency: 'USD',
        offerCount: '25'
      }
    }
  };

  return (
    <div className="home-page">
      {/* SEO Head */}
      <SEOHead
        title={homepageSEO.title}
        description={homepageSEO.description}
        keywords={homepageSEO.keywords}
        canonicalUrl={homepageSEO.canonicalUrl}
        ogType={homepageSEO.ogType}
        ogImage={homepageSEO.ogImage}
        structuredData={homepageSEO.structuredData}
      />
      
      {/* Hero Section */}
      <HeroSection />

      {/* Featured Tours Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 featured-tours-section">
        <div className="container-custom">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              Unforgettable Experiences
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">
              Featured Tours & Safaris
              <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary rounded-full"></span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-6">
              Explore our handpicked selection of premium tours and safari experiences across breathtaking destinations
            </p>
          </div>
          <FeaturedTours tours={featuredTours || placeholderTours} isLoading={isLoadingTours} />
        </div>
      </section>

      {/* Tour Categories Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <TourCategories />
        </div>
      </section>

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Testimonials Section */}
      <section className="section bg-gray-50 testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Travelers Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Read authentic reviews from travelers who have experienced our tours and services
            </p>
          </div>
          <Testimonials testimonials={testimonials || placeholderTestimonials} isLoading={isLoadingTestimonials} />
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Travel Tips & Insights</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Stay updated with the latest travel advice, destination guides, and safari experiences
            </p>
          </div>
          <BlogPreview posts={blogPosts || placeholderBlogPosts} isLoading={isLoadingBlogPosts} />
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction />
    </div>
  );
};

export default HomePage;
